'use client'

import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { useGetRestaurantQuery } from '@/api/restaurant/get-restaurant-query'
import { IDetailRestaurant } from '@/api/restaurants/types/restaurant-types'
import { RestaurantDetailSectionId } from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { IChildrenProps } from '@/typings/children-props'
import { noOp } from '@/utils/functions/noOp'

type IRestaurantDetailSectionRefs = Record<RestaurantDetailSectionId, HTMLDivElement | null>

interface IRestaurantDetailContext {
    restaurantSlug: string
    citySlug: string
    restaurant: IDetailRestaurant | undefined
    sectionRefs: IRestaurantDetailSectionRefs
    registerSection: (sectionId: string, ref: HTMLDivElement) => void
}

const DEFAULT_RESTAURANT_DETAIL_SECTION_REFS: IRestaurantDetailSectionRefs = {
    [RestaurantDetailSectionId.DEALS]: null,
    [RestaurantDetailSectionId.MENU]: null,
    [RestaurantDetailSectionId.REVIEWS]: null,
    [RestaurantDetailSectionId.INFO]: null,
    [RestaurantDetailSectionId.REWARDS]: null,
}

const RestaurantDetailContext = createContext<IRestaurantDetailContext>({
    restaurantSlug: '',
    citySlug: '',
    restaurant: undefined,
    registerSection: noOp,
    sectionRefs: DEFAULT_RESTAURANT_DETAIL_SECTION_REFS,
})

interface IRestaurantDetailProviderProps extends IChildrenProps {
    restaurantSlug: string
    citySlug: string
}

export const RestaurantDetailProvider = ({
    children,
    restaurantSlug,
    citySlug,
}: IRestaurantDetailProviderProps) => {
    const sectionRefs = useRef<Record<RestaurantDetailSectionId, HTMLDivElement | null>>(
        DEFAULT_RESTAURANT_DETAIL_SECTION_REFS
    )

    const registerSection = useCallback((sectionId: string, ref: HTMLDivElement) => {
        sectionRefs.current[sectionId as RestaurantDetailSectionId] = ref
    }, [])

    // Prefetched and hydrated by the server component above, so this reads from cache.
    const { data } = useGetRestaurantQuery(restaurantSlug)

    const restaurant = data?.data

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: 'instant',
            })
        }, 0)
    }, [])

    return (
        <RestaurantDetailContext.Provider
            value={{
                restaurantSlug,
                citySlug,
                restaurant: restaurant ?? undefined,
                registerSection,
                sectionRefs: sectionRefs.current,
            }}
        >
            {children}
        </RestaurantDetailContext.Provider>
    )
}

export const useRestaurantDetailContext = () => {
    return useContext(RestaurantDetailContext)
}
